import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { WARootState } from '@/store/store';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import {
  useGetUserSettingsQuery,
  useUpdateUserSettingMutation,
  useClearUserSettingMutation,
} from '@/store/settingsApi';

/**
 * Key format convention for user choices:
 * - Grouping: `{Role}{PageTitle}GroupBy` (e.g., "InvestorAccountsGroupBy")
 * - View: `{Role}{PageTitle}SelectView` (e.g., "InvestorAccountsSelectView")
 *
 * This matches the legacy UserChoicesManager key format from:
 * legacy-ui/Scripts/src/Shared/Data/UserChoicesManager.js
 */

export interface UseUserChoicesReturn {
  getValue: (key: string) => string | undefined;
  setValue: (key: string, value: string) => void;
  clearValue: (key: string) => void;
  isLoading: boolean;
  isSettingsReady: boolean;
  isError: boolean;
}

/**
 * Hook for reading and writing user choices (preferences) with optimistic updates.
 *
 * This is the new-ui equivalent of legacy's UserChoicesManager, but uses the
 * server-side /api/user/settings endpoint instead of localStorage
 *
 * @example
 * ```tsx
 * const { getValue, setValue, isLoading } = useUserChoices();
 *
 * // Read a saved grouping preference
 * const savedGroup = getValue("InvestorAccountsGroupBy");
 *
 * // Save a new preference (updates UI immediately)
 * const handleGroupChange = (newGroup: string) => {
 *   setValue("InvestorAccountsGroupBy", newGroup);
 * };
 * ```
 */
export function useUserChoices(): UseUserChoicesReturn {
  const currentRole = useSelector(
    (state: WARootState) => state.auth.currentRole,
  );
  const authLoaded = useAuthIsLoaded();

  const {
    data: settings,
    isLoading,
    isFetching,
    isError,
    isSuccess,
  } = useGetUserSettingsQuery(authLoaded ? undefined : skipToken);
  const [updateSetting] = useUpdateUserSettingMutation();
  const [clearSetting] = useClearUserSettingMutation();

  const isSettingsReady = isSuccess;

  const getValue = useCallback(
    (key: string): string | undefined => {
      if (!settings) {
        return undefined;
      }
      const exactMatch = settings.find((s) => s.Key === key);
      if (exactMatch) {
        return exactMatch.Value;
      }
      const roleKey = `${currentRole ?? ''}${key}`;
      const roleMatch = settings.find((s) => s.Key === roleKey);
      return roleMatch?.Value;
    },
    [settings, currentRole],
  );

  const setValue = useCallback(
    (key: string, value: string): void => {
      const roleKey = `${currentRole ?? ''}${key}`;
      void updateSetting({ Key: roleKey, Value: value });
    },
    [currentRole, updateSetting],
  );

  const clearValue = useCallback(
    (key: string): void => {
      const roleKey = `${currentRole ?? ''}${key}`;
      void clearSetting({ Key: roleKey });
    },
    [currentRole, clearSetting],
  );

  return {
    getValue,
    setValue,
    clearValue,
    isLoading: isLoading || isFetching,
    isSettingsReady,
    isError,
  };
}

/**
 * Generates a settings key for grouping preferences.
 * @param pageTitle - The page title (e.g., "Accounts", "Holdings")
 * @returns The key without role prefix (role is added by useUserChoices)
 */
export function getGroupByKey(pageTitle: string): string {
  return `${pageTitle.replace(/\s+/g, '')}GroupBy`;
}

/**
 * Generates a settings key for view mode preferences.
 * @param pageTitle - The page title (e.g., "Accounts", "Holdings")
 * @returns The key without role prefix (role is added by useUserChoices)
 */
export function getSelectViewKey(pageTitle: string): string {
  return `${pageTitle.replace(/\s+/g, '')}SelectView`;
}

export default useUserChoices;
